/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { DistributiveOmit } from '@direct-frontend/stdlib';
import { Reaction } from '@adv-frontend/reactions';

import { UnknownFormInstance } from '../form/types';
import { getDefaultValue, DefaultValueOrFactory } from '../utils/getDefaultValue';
import { ArrayFieldState, FieldState } from '../store/types';
import { BaseValidationError } from '../types';

import {
    $internals,
    BaseField,
    BaseFieldSelectorsMap,
    BoundFieldActions,
    BoundFieldAsyncActions,
    BoundFieldSelectors,
    FieldActionsMap,
    FieldAsyncActionsMap,
    FieldInstance,
    FieldSelectorsMap,
    IfFieldsRequireContextInput,
    InferFieldContextInput,
    InferFieldValue,
    SetValueOptions,
    SkipUnknownActions,
    SkipUnknownAsyncActions,
    SkipUnknownOrAny,
    SkipUnknownSelectors,
    SkipUnknownValidationError,
} from './types';
import { FieldValidator } from './validation';

/** Базовый тип для любого поля-массива */
export type BaseArrayField = ArrayField<any, any, never, any, any, any, any>;
/** Базовый тип для инстанса любого поля-массива */
export type BaseArrayFieldInstance = ArrayFieldInstance<any, any, any, any, any, any, any>;

/**
 * Поле-массив – представляет собой список полей заданного типа
 * Важно использовать `interface`, чтобы иметь возможность описать циклический тип
 * @see https://github.com/microsoft/TypeScript/pull/33050
 **/
export interface ArrayField<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError = never,
    FieldContextInput = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        FieldState<InferFieldValue<InnerField>[], FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]> = never,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            InnerField,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    > = never,
> {
    type: 'array';
    field: InnerField;
    defaultValue: InferFieldValue<InnerField>[];
    reactions?: ArrayFieldReaction<never, never, never, never, never, never>[];
    validators?: ArrayFieldValidators<
        InnerField,
        FieldValidationError,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;
    actions?: FieldActions;
    selectors?: FieldSelectors;
    asyncActions?: FieldAsyncActions;
    context?: IfFieldsRequireContextInput<
        { field: InnerField },
        (input: FieldContextInput) => { array: FieldContext; field: InferFieldContextInput<InnerField> },
        (input: FieldContextInput) => { array: FieldContext }
    >;
}

/**
 * Инстанс поля-массива без asyncActions (используется для разрыва циклической зависимости)
 * @internal
 **/
export type ArrayFieldInstanceWithoutAsyncActions<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        ArrayFieldState<InferFieldValue<InnerField>[], FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]> = never,
    FieldName extends string = string,
> = {
    /** Id инстанса поля */
    id: string;
    /** Тип поля */
    type: 'array';
    /** Название поля (может использоваться как дискриминант в юнионах) */
    name: FieldName;
    /** Полный путь до поля в форме */
    path: string[];
    /** Родительская форма поля */
    form: UnknownFormInstance;
    /**
     * Получает инстанс поля-элемента массива по его индексу
     * @param index Индекс элемента массива
     * @returns Инстанс поля-элемента массива
     **/
    fieldAt: (index: number) => FieldInstance<InnerField, FieldName>;
    /**
     * Обновляет значение всего массива
     * @param values Список значений элементов массива
     * @param options Параметры для внутреннего использования
     **/
    setValues: (values: InferFieldValue<InnerField>[], options?: SetValueOptions) => void;
    /**
     * Добавляет элемент в конец массива
     * @param value Значение элемента, который должен быть добавлен в конец массива
     * @param options Параметры для внутреннего использования
     **/
    addValue: (value: InferFieldValue<InnerField>, options?: SetValueOptions) => void;
    /**
     * Удаляет элемент из массива по его id
     * @param id Id элемента, который должен быть удален из массива
     * @param options Параметры для внутреннего использования
     **/
    removeValue: (id: string, options?: SetValueOptions) => void;
    /**
     * Вставляет элемент в массив по заданному индексу, сдвигая все последующие элементы
     * @param index Индекс, по которому должен быть вставлен новый элемент
     * @param value Значение, которое должно быть вставлено по заданному индексу
     * @param options Параметры для внутреннего использования
     **/
    insertValue: (index: number, value: InferFieldValue<InnerField>, options?: SetValueOptions) => void;
    /**
     * Обновляет значение элемента массива по его индексу
     * @param index Индекс, значение по которому должно быть заменено
     * @param value Значение, на которое должен быть заменен элемент по заданному индексу
     * @param options Параметры для внутреннего использования
     **/
    replaceValue: (index: number, value: InferFieldValue<InnerField>, options?: SetValueOptions) => void;
    /** Получает текущее значение массива */
    getValue: () => InferFieldValue<InnerField>[];
    /** Получает текущее состояние массива */
    getState: () => FieldState<InferFieldValue<InnerField>[], FieldValidationError>;
    /** Возвращает текущее значение контекста поля */
    getContext: () => FieldContext;
    /** Запускает повторную валидацию поля и всех его дочерних полей */
    revalidate: () => void;
    /** Добавляет ошибку к форме */
    addValidationError: (error: DistributiveOmit<FieldValidationError, 'meta'>) => void;
    /** Добавляет ошибки к форме */
    addValidationErrors: (errors: DistributiveOmit<FieldValidationError, 'meta'>[]) => void;
    /** Экшены поля */
    actions: BoundFieldActions<FieldActions>;
    /** Селекторы поля */
    selectors: BoundFieldSelectors<FieldSelectors>;
    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        /** Декларация поля */
        declaration: BaseArrayField;
        /** Получает текущее значение массива */
        useValue: () => InferFieldValue<InnerField>[];
        /** Получает текущее состояние массива */
        useState: () => FieldState<InferFieldValue<InnerField>[], FieldValidationError>;
        /** Возвращает текущее значение контекста поля */
        useContext: () => FieldContext;
        /** Выставляет состояние isTouched у поля */
        touch: () => void;
        /** Скроллит страницу к полю */
        scrollToField: () => void;
    };
};

/**
 * Инстанс поля-массива – предоставляет методы для работы с элементами массива
 * @typeParam InnerField Дочернее поле массива
 * @typeParam FieldValidationError Допустимые ошибки валидации массива
 * @typeParam FieldContext Контекст поля
 * @typeParam FieldActions Экшены поля
 * @typeParam FieldSelectors Селекторы поля
 * @typeParam FieldName Название поля внутри родительского поля или формы
 **/
export type ArrayFieldInstance<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError = never,
    FieldContext = never,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        ArrayFieldState<InferFieldValue<InnerField>[], FieldValidationError>,
        FieldContext
    > = never,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]> = never,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            InnerField,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    > = never,
    FieldName extends string = string,
> = ArrayFieldInstanceWithoutAsyncActions<
    InnerField,
    FieldValidationError,
    FieldContext,
    FieldActions,
    FieldSelectors,
    FieldName
> & {
    /** Асинхронные экшены поля */
    asyncActions: BoundFieldAsyncActions<FieldAsyncActions>;
};

/** Объект со всеми валидаторами поля-массива */
type ArrayFieldValidators<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldSelectors extends BaseFieldSelectorsMap,
> = {
    /** Валидаторы, которые будут запущены при изменении любого значения в массиве */
    onChange?: FieldValidator<
        ArrayField<InnerField, FieldValidationError, FieldContextInput, FieldContext, never, FieldSelectors, never>
    >[];
    /** Валидаторы, которые будут запущены при отправке формы */
    onSubmit?: FieldValidator<
        ArrayField<InnerField, FieldValidationError, FieldContextInput, FieldContext, never, FieldSelectors, never>
    >[];
};

/** Реакция поля-массива */
export type ArrayFieldReaction<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        FieldState<InferFieldValue<InnerField>[], FieldValidationError>,
        FieldContext
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            InnerField,
            FieldValidationError,
            FieldContext,
            FieldActions,
            FieldSelectors
        >
    >,
> = Reaction<
    any,
    ArrayFieldInstance<InnerField, FieldValidationError, FieldContext, FieldActions, FieldSelectors, FieldAsyncActions>
>;

/** Параметры для создания поля-массива */
type ArrayFieldOptions<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            NoInfer<InnerField>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldValue<InnerField>[],
                FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>
        >
    >,
> = {
    /** Поле, которое является элементом массива */
    field: InnerField | (() => InnerField);
    /**
     * Значение поля по умолчанию (будет использовано если начальное значение формы не задано)
     * Если не указано, то значением по умолчанию будет пустой массив
     **/
    defaultValue?: DefaultValueOrFactory<InferFieldValue<InnerField>[]>;
    /** Список валидаторов поля */
    validators?: (
        // Используем интерфейс с функцией, чтобы иметь возможность указать значение type-параметра руками
        createValidators: <PassedFieldValidationError extends BaseValidationError>(
            validators?: ArrayFieldValidators<
                InnerField,
                PassedFieldValidationError,
                FieldContextInput,
                FieldContext,
                never
            >
        ) => ArrayFieldValidators<InnerField, PassedFieldValidationError, FieldContextInput, FieldContext, never>
    ) => ArrayFieldValidators<
        InnerField,
        SkipUnknownValidationError<FieldValidationError>,
        FieldContextInput,
        FieldContext,
        FieldSelectors
    >;

    /** Экшены поля */
    actions?: FieldActions;
    /** Селекторы поля */
    selectors?: FieldSelectors;
    /** Асинхронные экшены поля */
    asyncActions?: (
        createAsyncActions: <
            T extends FieldAsyncActionsMap<
                ArrayFieldInstanceWithoutAsyncActions<
                    NoInfer<InnerField>,
                    SkipUnknownValidationError<FieldValidationError>,
                    SkipUnknownOrAny<FieldContext>,
                    SkipUnknownActions<
                        InferFieldValue<InnerField>[],
                        FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
                        SkipUnknownOrAny<FieldContext>,
                        NoInfer<FieldActions>
                    >,
                    SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>
                >
            >,
        >(
            asyncActions: T
        ) => T
    ) => FieldAsyncActions;

    /** Список реакций поля */
    reactions?: (
        createReactions: (
            reactions: ArrayFieldReaction<
                NoInfer<InnerField>,
                SkipUnknownValidationError<FieldValidationError>,
                SkipUnknownOrAny<FieldContext>,
                SkipUnknownActions<
                    InferFieldValue<InnerField>[],
                    FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
                    SkipUnknownOrAny<FieldContext>,
                    NoInfer<FieldActions>
                >,
                SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>,
                SkipUnknownAsyncActions<FieldAsyncActions>
            >[]
        ) => ArrayFieldReaction<
            NoInfer<InnerField>,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldValue<InnerField>[],
                FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>,
            SkipUnknownAsyncActions<FieldAsyncActions>
        >[]
    ) => ArrayFieldReaction<
        NoInfer<InnerField>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContext>,
        SkipUnknownActions<
            InferFieldValue<InnerField>[],
            FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
            SkipUnknownOrAny<FieldContext>,
            NoInfer<FieldActions>
        >,
        SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>,
        SkipUnknownAsyncActions<FieldAsyncActions>
    >[];
} & IfFieldsRequireContextInput<
    { field: InnerField },
    {
        /** Функция, наполняющая контекст всего массива и каждого его элемента */
        context: (input: FieldContextInput) => { array?: FieldContext; field: InferFieldContextInput<InnerField> };
    },
    {
        /** Функция, наполняющая контекст всего массива */
        context?: (input: FieldContextInput) => { array?: FieldContext };
    }
>;

/** Создает поле-массив */
export function createArrayField<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContextInput,
    FieldContext,
    FieldActions extends FieldActionsMap<
        InferFieldValue<InnerField>[],
        FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>
    >,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<
            InnerField,
            SkipUnknownValidationError<FieldValidationError>,
            SkipUnknownOrAny<FieldContext>,
            SkipUnknownActions<
                InferFieldValue<InnerField>[],
                FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
                SkipUnknownOrAny<FieldContext>,
                NoInfer<FieldActions>
            >,
            SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>
        >
    >,
>({
    actions,
    asyncActions,
    selectors,
    context,
    defaultValue,
    field,
    validators,
    reactions,
}: ArrayFieldOptions<
    InnerField,
    FieldValidationError,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    FieldActions,
    FieldSelectors,
    FieldAsyncActions
>): ArrayField<
    NoInfer<InnerField>,
    SkipUnknownValidationError<FieldValidationError>,
    SkipUnknownOrAny<FieldContextInput>,
    SkipUnknownOrAny<FieldContext>,
    SkipUnknownActions<
        InferFieldValue<InnerField>[],
        FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
        SkipUnknownOrAny<FieldContext>,
        NoInfer<FieldActions>
    >,
    SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>,
    SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
> {
    // Приводим тип к корректному, так как в возвращаемом значении параметры уточняются (удаляются any и unknown)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return {
        type: 'array',
        // Делаем поле геттером чтобы была возможность описывать поля с рекурсивной вложенностью
        // И не было ошибки Cannot access variable before initialization
        get field() {
            return typeof field === 'function' ? field() : field;
        },
        context,
        actions,
        asyncActions: asyncActions?.(asyncActionsArg => asyncActionsArg),
        selectors,
        defaultValue: getDefaultValue(defaultValue) ?? [],
        reactions: reactions?.(reaction => reaction),
        validators: validators?.(value => value ?? {}),
    } as ArrayField<
        NoInfer<InnerField>,
        SkipUnknownValidationError<FieldValidationError>,
        SkipUnknownOrAny<FieldContextInput>,
        SkipUnknownOrAny<FieldContext>,
        SkipUnknownActions<
            InferFieldValue<InnerField>[],
            FieldState<InferFieldValue<InnerField>[], SkipUnknownValidationError<FieldValidationError>>,
            SkipUnknownOrAny<FieldContext>,
            NoInfer<FieldActions>
        >,
        SkipUnknownSelectors<InferFieldValue<InnerField>[], NoInfer<FieldSelectors>>,
        SkipUnknownAsyncActions<NoInfer<FieldAsyncActions>>
    >;
}
